import { Mutex } from "async-mutex";
import { applyPatch, Operation } from "rfc6902";
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import * as SharedChatResponse from "../../../darvin-shared/schema/zod/agent/chat-response.schema";
import * as MessageShared from "../../../darvin-shared/schema/zod/agent/message-types.schema";
import * as SharedMessageTypes from "../../../darvin-shared/schema/zod/agent/message-types.schema";
import { ErrorContent as ErrorContentShared } from "../../../darvin-shared/schema/zod/agent/message-types.schema";
import {
  HistoryLoadingStatus,
  useProjectStore,
} from "../../core_stores/useProjectStore";
import { ImageService } from "../../services/imageService";
import {
  AssistantMessageAnimationStatus,
  ProjectChatAssistantMessage,
  ProjectChatAssistantMsgType,
  ProjectChatElement,
  ProjectChatException,
  ProjectChatTemporaryUserMessage,
  ProjectChatUserMessage,
} from "./chatModels";

interface ChatMessagesActions {
  startListening: () => void;
  addTemporaryMessage: (
    message: string,
    attachedImg: File | null,
  ) => Promise<void>;
  removeTemporaryMessages: () => Promise<void>;
  onAssistantMessageAnimationFinished: () => Promise<void>;
  addPreviewError: (error: string) => Promise<void>;
  dispose: () => Promise<void>;
  setShouldAutoScroll: (value: boolean) => Promise<void>;
  loadMoreMessages: () => Promise<void>;
}

interface ChatMessagesState {
  chatElements: {
    elements: ProjectChatElement[];
    revision: number;
  };
  lastUserMessage: ProjectChatUserMessage | null;
  hasMultipleUserMessages: boolean;
  exception: ProjectChatException | null;
  shouldAutoScroll: boolean;

  actions: ChatMessagesActions;
}

export const useChatMessagesStore = create<ChatMessagesState>()(
  subscribeWithSelector((set, get) => {
    let unsubscribeHistory: (() => void) | null = null;
    let unsubscribeLatestMsg: (() => void) | null = null;
    const processedMessages: Set<SharedChatResponse.MessageBody> = new Set();
    const stateMutex = new Mutex();
    const updateMutex = new Mutex();

    // Helper: Find last user message in chat elements
    function findLastUserMessage(
      chatElements: ProjectChatElement[],
    ): ProjectChatUserMessage | null {
      for (let i = chatElements.length - 1; i >= 0; i--) {
        if (chatElements[i] instanceof ProjectChatUserMessage) {
          return chatElements[i] as ProjectChatUserMessage;
        }
      }
      return null;
    }

    // Helper: Find last user message and check if multiple exist in chat elements
    function analyzeUserMessages(chatElements: ProjectChatElement[]): {
      lastUserMessage: ProjectChatUserMessage | null;
      hasMultipleInElements: boolean;
    } {
      let lastUserMessage: ProjectChatUserMessage | null = null;
      let foundCount = 0;

      // Single pass from end - stop when we find 2 messages
      for (let i = chatElements.length - 1; i >= 0; i--) {
        if (chatElements[i] instanceof ProjectChatUserMessage) {
          foundCount++;
          if (!lastUserMessage) {
            lastUserMessage = chatElements[i] as ProjectChatUserMessage;
          }
          // Early exit - we have enough info
          if (foundCount >= 2) {
            return { lastUserMessage, hasMultipleInElements: true };
          }
        }
      }

      return { lastUserMessage, hasMultipleInElements: false };
    }

    const getState = () => stateMutex.runExclusive(get);

    // Wrapper around set() that auto-updates lastUserMessage and hasMultipleUserMessages when chatElements change
    const setState = (
      partial:
        | Partial<ChatMessagesState>
        | ((state: ChatMessagesState) => Partial<ChatMessagesState>),
      options?: { isHistoryUpdate?: boolean },
    ) => {
      return stateMutex.runExclusive(async () => {
        const currentState = get();
        const update =
          typeof partial === "function" ? partial(currentState) : partial;

        // If chatElements changed AND lastUserMessage/hasMultipleUserMessages not explicitly provided, calculate them
        if (
          "chatElements" in update &&
          update.chatElements !== undefined &&
          !("lastUserMessage" in update)
        ) {
          const chatElements = update.chatElements.elements;

          // Handle empty array - clear lastUserMessage and hasMultipleUserMessages
          // Consider only non-temporary messages
          const nonTempElements = chatElements.filter(
            (e) => !(e instanceof ProjectChatTemporaryUserMessage),
          );
          if (nonTempElements.length === 0) {
            set({
              ...update,
              lastUserMessage: null,
              hasMultipleUserMessages: false,
            });
            return;
          }

          // Check if any assistant messages are animating
          const isAnimating = chatElements.some(
            (el) =>
              el instanceof ProjectChatAssistantMessage &&
              el.animationStatus === AssistantMessageAnimationStatus.ANIMATING,
          );

          // If hasMultipleUserMessages is already true, it can't flip back to false without history reload
          // Skip analysis and API calls - just find the last user message if not animating
          if (currentState.hasMultipleUserMessages) {
            if (!isAnimating) {
              // Find last user message without counting (we already know there are multiple)
              const lastUserMessage = findLastUserMessage(chatElements);
              set({
                ...update,
                lastUserMessage,
                hasMultipleUserMessages: true, // Keep it true
              });
            } else {
              // Don't update lastUserMessage while animating
              set(update);
            }
            return;
          }

          // hasMultipleUserMessages is false - we may need to check
          // Only analyze and call API during history updates, NOT during SSE streaming
          if (options?.isHistoryUpdate) {
            // Analyze user messages in chat elements
            const { lastUserMessage, hasMultipleInElements } =
              analyzeUserMessages(chatElements);

            // If we have 2+ user messages visible, we know there are multiple
            if (hasMultipleInElements) {
              set({
                ...update,
                lastUserMessage,
                hasMultipleUserMessages: true,
              });
            } else {
              // Less than 2 visible - fetch from API to check DB
              const apiResult = await useProjectStore
                .getState()
                .actions.loadLastUserMessage();

              // Use the most recent lastUserMessage (from elements if available, otherwise from API)
              const finalLastUserMessage = lastUserMessage
                ? lastUserMessage
                : apiResult.lastUserMessage
                  ? new ProjectChatUserMessage(
                      apiResult.lastUserMessage.messageId,
                      apiResult.lastUserMessage.timestamp,
                      apiResult.lastUserMessage.content,
                      apiResult.lastUserMessage.checkpointId!,
                    )
                  : null;

              set({
                ...update,
                lastUserMessage: finalLastUserMessage,
                hasMultipleUserMessages: apiResult.hasMore,
              });
            }
          } else {
            // SSE streaming - no API calls, but check if hasMultipleUserMessages can flip from false to true
            if (!isAnimating) {
              // Analyze to check if we now have 2+ visible user messages
              const { lastUserMessage, hasMultipleInElements } =
                analyzeUserMessages(chatElements);

              set({
                ...update,
                lastUserMessage,
                hasMultipleUserMessages: hasMultipleInElements,
              });
            } else {
              // Don't update lastUserMessage while animating
              set(update);
            }
          }
        } else {
          // Either no chatElements change OR lastUserMessage explicitly provided - just pass through
          set(update);
        }
      });
    };

    function hasMessageBeenProcessed(
      message: SharedChatResponse.MessageBody,
      chatElements: ProjectChatElement[],
    ): boolean {
      for (const processedMessage of processedMessages) {
        if (
          chatElements.some((chatEl) => chatEl.id === message.messageId) &&
          JSON.stringify(message) === JSON.stringify(processedMessage)
        ) {
          return true;
        }
      }
      return false;
    }

    // Helper: Process history messages into chat elements
    function processHistoryMessages(
      history: SharedChatResponse.MessageBody[],
      currentChatElements: ProjectChatElement[],
    ): ProjectChatElement[] {
      const updatedChatElements = [...currentChatElements];

      for (const message of history) {
        if (hasMessageBeenProcessed(message, currentChatElements)) {
          continue;
        }
        processMessage(message, updatedChatElements, false);
      }

      return updatedChatElements;
    }

    // Helper: Finalize chat elements (sort and update animation status)
    function finalizeChatElements(
      chatElements: ProjectChatElement[],
    ): ProjectChatElement[] {
      let finalized = sortMessages(chatElements);
      finalized = updateAnimationStatusOfMessages(finalized);
      return finalized;
    }

    // Helper: Partition chat elements in a single pass - O(n) instead of O(n*m)
    function partitionChatElements(
      history: SharedChatResponse.MessageBody[],
      chatElements: ProjectChatElement[],
    ): {
      existingInHistory: ProjectChatElement[];
      notInHistory: ProjectChatElement[];
      temporary: ProjectChatElement[];
    } {
      // Create a Set for O(1) lookup instead of O(m) for history.some()
      const historyMessageIds = new Set(history.map((msg) => msg.messageId));

      const existingInHistory: ProjectChatElement[] = [];
      const notInHistory: ProjectChatElement[] = [];
      const temporary: ProjectChatElement[] = [];

      // Single pass through chat elements
      for (const element of chatElements) {
        // Keep temporary messages separate - they should never be processed
        if (element instanceof ProjectChatTemporaryUserMessage) {
          temporary.push(element);
        } else if (historyMessageIds.has(element.id)) {
          existingInHistory.push(element);
        } else {
          notInHistory.push(element);
        }
      }

      return { existingInHistory, notInHistory, temporary };
    }

    async function historyExtended(history: SharedChatResponse.MessageBody[]) {
      const state = await getState();
      const currentChatElements = state.chatElements.elements;

      // Empty history edge case
      if (history.length === 0) {
        const { notInHistory, temporary } = partitionChatElements(
          history,
          currentChatElements,
        );
        await setState(
          {
            chatElements: {
              ...state.chatElements,
              elements: [...notInHistory, ...temporary],
            },
          },
          { isHistoryUpdate: true },
        );
        return;
      }

      // Single pass: partition elements into those in history vs not vs temporary
      const { existingInHistory, notInHistory, temporary } =
        partitionChatElements(history, currentChatElements);

      // Process history messages (without temporary messages)
      let updatedChatElements = processHistoryMessages(
        history,
        existingInHistory,
      );

      // Create Set for O(1) lookup when checking duplicates
      const updatedIds = new Set(updatedChatElements.map((el) => el.id));

      // Re-add SSE messages that weren't in history (single pass, O(1) lookup)
      for (const element of notInHistory) {
        if (!updatedIds.has(element.id)) {
          updatedChatElements.push(element);
        }
      }

      // Always re-add temporary messages at the end
      updatedChatElements.push(...temporary);

      // Finalize and set
      updatedChatElements = finalizeChatElements(updatedChatElements);
      await setState(
        {
          chatElements: {
            ...state.chatElements,
            elements: updatedChatElements,
          },
        },
        { isHistoryUpdate: true },
      );
    }

    async function historyReloaded(history: SharedChatResponse.MessageBody[]) {
      const state = await getState();
      const currentElements = state.chatElements.elements;

      // Preserve temporary messages during reload
      const temporary = currentElements.filter(
        (el) => el instanceof ProjectChatTemporaryUserMessage,
      );

      // Empty history edge case
      if (history.length === 0) {
        await setState(
          {
            chatElements: {
              elements: [...temporary],
              revision: 0,
            },
          },
          { isHistoryUpdate: true },
        );
        return;
      }

      // Start fresh and process all history messages
      let chatElements = processHistoryMessages(history, []);

      // Re-add temporary messages at the end
      chatElements.push(...temporary);

      // Finalize and set
      chatElements = finalizeChatElements(chatElements);
      await setState(
        {
          chatElements: {
            elements: chatElements,
            revision: state.chatElements.revision + 1,
          },
        },
        { isHistoryUpdate: true },
      );
    }

    async function historyUpdated(
      history: SharedChatResponse.MessageBody[],
      isReload: boolean,
    ) {
      if (isReload) {
        await historyReloaded(history);
      } else {
        await historyExtended(history);
      }
    }

    async function messageReceived(msg: SharedChatResponse.MessageBody) {
      const state = await getState();
      const currentElements = state.chatElements.elements;

      if (hasMessageBeenProcessed(msg, currentElements)) {
        return;
      }

      let updatedChatElements = processMessage(msg, currentElements, true);
      updatedChatElements = sortMessages(updatedChatElements);
      updatedChatElements =
        updateAnimationStatusOfMessages(updatedChatElements);

      if (updatedChatElements.length > 1) {
        const lastElement = updatedChatElements[updatedChatElements.length - 1];
        const secondToLastElement =
          updatedChatElements[updatedChatElements.length - 2];
        if (
          lastElement instanceof ProjectChatAssistantMessage &&
          secondToLastElement instanceof ProjectChatAssistantMessage &&
          lastElement.type === "error" &&
          secondToLastElement.type === "error" &&
          (lastElement.value as ErrorContentShared).type ===
            SharedMessageTypes.ErrorTypeEnum.enum.technical &&
          (secondToLastElement.value as ErrorContentShared).type ===
            SharedMessageTypes.ErrorTypeEnum.enum.technical &&
          (secondToLastElement.value as ErrorContentShared).message ===
            (lastElement.value as ErrorContentShared).message
        ) {
          // There are two duplicated technical errors, so remove only second to last one
          updatedChatElements.splice(updatedChatElements.length - 2, 1);
        }
      }

      processedMessages.add(msg);

      // setState has its own mutex protection
      await setState({
        chatElements: {
          ...state.chatElements,
          elements: updatedChatElements,
        },
      });
    }

    function processMessage(
      msg: SharedChatResponse.MessageBody,
      updatedChatElements: ProjectChatElement[],
      canBeAnimated: boolean,
    ): ProjectChatElement[] {
      // Remove temporary user message if it exists
      if (msg.type === SharedChatResponse.MessageTypeEnum.enum.userMessage) {
        const tempMsgIndex = updatedChatElements.findIndex(
          (el) => el instanceof ProjectChatTemporaryUserMessage,
        );
        if (tempMsgIndex !== -1) {
          updatedChatElements.splice(tempMsgIndex, 1);
        }
      }

      const chatElement: ProjectChatElement | undefined =
        mapMessageToChatElement(msg, updatedChatElements, canBeAnimated);
      if (chatElement !== undefined) {
        const existingElementIndex = updatedChatElements.findIndex(
          (el) => el.id === chatElement.id,
        );

        if (existingElementIndex === -1) {
          if (updatedChatElements.length > 0) {
            const lastElement =
              updatedChatElements[updatedChatElements.length - 1];
            if (
              lastElement instanceof ProjectChatAssistantMessage &&
              !lastElement.fullyGenerated
            ) {
              lastElement.fullyGenerated = true;
            }
          }
          updatedChatElements.push(chatElement);
        } else {
          updatedChatElements[existingElementIndex] = chatElement;
        }
      }

      processedMessages.add(msg);
      return updatedChatElements;
    }

    function sortMessages(updatedChatElements: ProjectChatElement[]) {
      return updatedChatElements.sort((a, b) => a.timestamp - b.timestamp);
    }

    function updateAnimationStatusOfMessages(
      chatElements: ProjectChatElement[],
    ): ProjectChatElement[] {
      const updatedChatElements = [...chatElements];
      const isNoAssistantElementAnimating =
        updatedChatElements.findIndex(
          (el) =>
            el instanceof ProjectChatAssistantMessage &&
            el.animationStatus === AssistantMessageAnimationStatus.ANIMATING,
        ) === -1;
      if (isNoAssistantElementAnimating) {
        const indexOfFirstWaitingAssistantElement =
          updatedChatElements.findIndex(
            (el) =>
              el instanceof ProjectChatAssistantMessage &&
              el.animationStatus === AssistantMessageAnimationStatus.WAITING,
          );
        if (indexOfFirstWaitingAssistantElement > -1) {
          (
            updatedChatElements[
              indexOfFirstWaitingAssistantElement
            ] as ProjectChatAssistantMessage
          ).animationStatus = AssistantMessageAnimationStatus.ANIMATING;
        }
      }
      return updatedChatElements;
    }

    function mapMessageToChatElement(
      message: SharedChatResponse.MessageBody,
      chatElements: ProjectChatElement[],
      canBeAnimated: boolean,
    ): ProjectChatElement | undefined {
      if (
        message.type === SharedChatResponse.MessageTypeEnum.enum.userMessage
      ) {
        return new ProjectChatUserMessage(
          message.messageId,
          message.timestamp,
          message.content,
          message.checkpointId!,
        );
      } else {
        let msgType: ProjectChatAssistantMsgType;
        switch (message.type) {
          case SharedChatResponse.MessageTypeEnum.enum.general:
            msgType = "general";
            break;
          case SharedChatResponse.MessageTypeEnum.enum.tools:
            msgType = "tools";
            break;
          case SharedChatResponse.MessageTypeEnum.enum.projectConfig:
            msgType = "projectConfig";
            break;
          case SharedChatResponse.MessageTypeEnum.enum.error:
            msgType = "error";
            break;
          default:
            throw new Error(`Unknown message type: ${message.type}`);
        }

        const existingMsg = chatElements.find(
          (el) => el.id === message.messageId,
        ) as ProjectChatAssistantMessage | undefined;
        const content = message.stream
          ? applyDeltas(
              message.type,
              existingMsg?.value as
                | SharedMessageTypes.GeneralAssistantMessage
                | SharedMessageTypes.Tools
                | undefined,
              message.chunk!.delta as Operation[],
            )
          : message.content;

        if (
          Object.keys(content).length === 0 ||
          ("files" in content && Object.keys(content.files).length === 0)
        ) {
          return undefined;
        }

        let animationStatus = AssistantMessageAnimationStatus.WAITING;
        if (existingMsg !== undefined) {
          animationStatus = existingMsg.animationStatus;
        } else if (
          (chatElements.length > 0 &&
            message.timestamp <
              chatElements[chatElements.length - 1].timestamp) ||
          !canBeAnimated
        ) {
          animationStatus = AssistantMessageAnimationStatus.DISPLAYED;
        } else if (canBeAnimated) {
          animationStatus = AssistantMessageAnimationStatus.WAITING;
        }

        return new ProjectChatAssistantMessage(
          message.messageId,
          message.timestamp,
          animationStatus,
          existingMsg?.visibleWords ?? 0,
          message.stream ? message.chunk!.done : true,
          content,
          msgType,
        );
      }
    }

    function applyDeltas(
      type: SharedChatResponse.MessageType,
      existingValue:
        | SharedMessageTypes.GeneralAssistantMessage
        | SharedMessageTypes.Tools
        | undefined,
      delta: Operation[],
    ): SharedMessageTypes.GeneralAssistantMessage | SharedMessageTypes.Tools {
      if (type === SharedChatResponse.MessageTypeEnum.enum.tools) {
        const value: SharedMessageTypes.Tools =
          (existingValue as SharedMessageTypes.Tools) ?? { toolUses: {} };
        applyPatch(value, delta as Operation[]);
        return value;
      } else {
        const value: SharedMessageTypes.GeneralAssistantMessage =
          (existingValue as SharedMessageTypes.GeneralAssistantMessage) ??
          ({} as SharedMessageTypes.GeneralAssistantMessage);
        applyPatch(value, delta as Operation[]);
        return value;
      }
    }

    function isMessageToShowOnChat(
      message: SharedChatResponse.MessageBody,
    ): boolean {
      return (
        message.type === SharedChatResponse.MessageTypeEnum.enum.userMessage ||
        message.type === SharedChatResponse.MessageTypeEnum.enum.general ||
        message.type ===
          SharedChatResponse.MessageTypeEnum.enum.projectConfig ||
        message.type === SharedChatResponse.MessageTypeEnum.enum.tools ||
        message.type === SharedChatResponse.MessageTypeEnum.enum.error
      );
    }

    return {
      chatElements: {
        elements: [],
        revision: 0,
      },
      lastUserMessage: null,
      hasMultipleUserMessages: false,
      exception: null,
      shouldAutoScroll: true,

      actions: {
        addTemporaryMessage: async (
          message: string,
          attachedImg: File | null,
        ) => {
          await setState({ shouldAutoScroll: true });
          const base64 = attachedImg
            ? await ImageService.convertImageToBase64(attachedImg)
            : null;
          const tempMessage = new ProjectChatTemporaryUserMessage(
            `temp-${Date.now()}`,
            Date.now(),
            message,
            base64
              ? MessageShared.UserMessageAttachmentSchema.parse({
                  content: base64,
                  mimeType: attachedImg?.type ?? undefined,
                  filename: attachedImg?.name ?? undefined,
                })
              : null,
          );
          await setState((state) => {
            // Filter out any existing temporary messages
            const filteredChatElements = state.chatElements.elements.filter(
              (el) => !(el instanceof ProjectChatTemporaryUserMessage),
            );
            const updatedChatElements = [...filteredChatElements, tempMessage];
            return {
              chatElements: {
                ...state.chatElements,
                elements: updatedChatElements,
              },
              shouldAutoScroll: true,
            };
          });
        },

        removeTemporaryMessages: async () => {
          await setState((state) => {
            const filteredChatElements = state.chatElements.elements.filter(
              (el) => !(el instanceof ProjectChatTemporaryUserMessage),
            );
            return {
              chatElements: {
                ...state.chatElements,
                elements: filteredChatElements,
              },
            };
          });
        },

        startListening: () => {
          if (unsubscribeHistory && unsubscribeLatestMsg) {
            return;
          }

          unsubscribeHistory = useProjectStore.subscribe(
            (state) => state.history,
            async (history) => {
              if (history) {
                const messagesToShow = history.filter(isMessageToShowOnChat);
                // Derive isReload from historyLoadingStatus:
                // LOADING = reload (clear and rebuild)
                // LOADING_MORE = extend (merge with existing)
                const historyLoadingStatus =
                  useProjectStore.getState().historyLoadingStatus;
                const isReload =
                  historyLoadingStatus === HistoryLoadingStatus.LOADING;
                await updateMutex.runExclusive(async () => {
                  await historyUpdated(messagesToShow, isReload);
                });
              }
            },
          );
          unsubscribeLatestMsg = useProjectStore.subscribe(
            (state) => state.latestMsg,
            async (latestMsg) => {
              if (latestMsg && isMessageToShowOnChat(latestMsg)) {
                await updateMutex.runExclusive(async () => {
                  await messageReceived(latestMsg);
                });
              }
            },
            {
              equalityFn: (a, b) =>
                a?.messageId === b?.messageId && a?.chunk?.id === b?.chunk?.id,
            },
          );
        },

        dispose: async () => {
          if (unsubscribeHistory) {
            unsubscribeHistory();
          }
          unsubscribeHistory = null;
          if (unsubscribeLatestMsg) {
            unsubscribeLatestMsg();
          }
          unsubscribeLatestMsg = null;

          processedMessages.clear();
          await setState({
            chatElements: {
              elements: [],
              revision: 0,
            },
            exception: null,
            shouldAutoScroll: true,
          });
        },

        onAssistantMessageAnimationFinished: async () => {
          const state = await getState();
          const updatedChatElements = [...state.chatElements.elements];
          const animatingMessageIndex = updatedChatElements.findIndex(
            (el) =>
              el instanceof ProjectChatAssistantMessage &&
              el.animationStatus === AssistantMessageAnimationStatus.ANIMATING,
          );
          if (animatingMessageIndex !== -1) {
            const animatingMessage = updatedChatElements[
              animatingMessageIndex
            ] as ProjectChatAssistantMessage;
            if (animatingMessage.fullyGenerated) {
              animatingMessage.animationStatus =
                AssistantMessageAnimationStatus.DISPLAYED;
              if (animatingMessageIndex + 1 < updatedChatElements.length) {
                (
                  updatedChatElements[
                    animatingMessageIndex + 1
                  ] as ProjectChatAssistantMessage
                ).animationStatus = AssistantMessageAnimationStatus.ANIMATING;
              }
            }
          }
          await setState({
            chatElements: {
              ...state.chatElements,
              elements: updatedChatElements,
            },
          });
        },

        addPreviewError: async (error: string) => {
          const state = await getState();
          const currentElements = state.chatElements.elements;

          const doesPreviewErrorAlreadyExist = currentElements.some(
            (el) =>
              el instanceof ProjectChatAssistantMessage &&
              el.id === `preview-error`,
          );
          if (doesPreviewErrorAlreadyExist) {
            return;
          }

          const isAnyMsgAnimating = currentElements.some(
            (el) =>
              el instanceof ProjectChatAssistantMessage &&
              el.animationStatus === AssistantMessageAnimationStatus.ANIMATING,
          );

          await setState({
            chatElements: {
              ...state.chatElements,
              elements: [
                ...currentElements,
                new ProjectChatAssistantMessage(
                  `preview-error`,
                  Date.now(),
                  isAnyMsgAnimating
                    ? AssistantMessageAnimationStatus.WAITING
                    : AssistantMessageAnimationStatus.ANIMATING,
                  0,
                  true,
                  {
                    type: "runtime",
                    message: error,
                  },
                  "error",
                ),
              ],
            },
          });
        },

        setShouldAutoScroll: async (value: boolean) => {
          await setState({ shouldAutoScroll: value });
        },

        loadMoreMessages: async () => {
          await useProjectStore.getState().actions.loadMoreHistory();
        },
      },
    };
  }),
);

export const useChatElements = () =>
  useChatMessagesStore((state) => state.chatElements.elements);

// Full chat elements (not paginated) - used for rollback button visibility logic
export const useAllChatElements = () =>
  useChatMessagesStore((state) => state.chatElements.elements);

export const useChatException = () =>
  useChatMessagesStore((state) => state.exception);

export const useLastUserMessage = () =>
  useChatMessagesStore((state) => state.lastUserMessage);

export const useHasMultipleUserMessages = () =>
  useChatMessagesStore((state) => state.hasMultipleUserMessages);

export const useShouldAutoScroll = () =>
  useChatMessagesStore((state) => state.shouldAutoScroll);

export const useHasMoreChatElements = () =>
  useProjectStore((state) => state.hasMoreHistory);

export const useIsLoadingMoreChatElements = () =>
  useProjectStore(
    (state) => state.historyLoadingStatus === HistoryLoadingStatus.LOADING_MORE,
  );

export const useChatMessagesActions = () =>
  useChatMessagesStore((state) => state.actions);
